/**
 * Addons Tab
 *
 * Displays contract add-ons
 */

import { Loader2, Package } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { useContractAddons } from '../../hooks/useContracts'

interface AddonsTabProps {
  contractId: string
}

export function AddonsTab({ contractId }: AddonsTabProps) {
  const { data: addons, isLoading, error } = useContractAddons(contractId)

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount)
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <Loader2 className='h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground' />
          <p className='text-muted-foreground'>Loading add-ons...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <p className='text-red-600 font-medium'>Failed to load add-ons</p>
          <p className='text-sm text-muted-foreground mt-1'>{error.message}</p>
        </div>
      </div>
    )
  }

  if (!addons || addons.length === 0) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <Package className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
          <p className='text-muted-foreground font-medium'>No add-ons</p>
          <p className='text-sm text-muted-foreground mt-1'>
            This contract has no add-ons
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {addons.map(addon => (
        <div key={addon.id} className='border rounded-lg p-4 space-y-3'>
          <div className='flex items-start justify-between'>
            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <h4 className='font-medium'>{addon.name}</h4>
                <Badge variant='secondary'>Add-on</Badge>
              </div>
              {addon.description && (
                <p className='text-sm text-muted-foreground'>
                  {addon.description}
                </p>
              )}
            </div>
            {addon.amount > 0 && (
              <div className='text-right'>
                <div className='font-semibold'>
                  {formatCurrency(addon.amount, addon.currency || 'USD')}
                </div>
              </div>
            )}
          </div>

          {/* Additional addon details */}
          {addon.metadata && Object.keys(addon.metadata).length > 0 && (
            <div className='border-t pt-3'>
              <div className='text-xs text-muted-foreground'>
                <pre className='overflow-auto'>
                  {JSON.stringify(addon.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
